import { useEffect, useState } from "react";
import { supabase } from "../lib/SupabaseClient";

interface Test {
    id: number;
    name: string;
}

interface Question {
    id: number;
    text: string;
    bank_id: number;
}

interface Assigned {
    question_id: number;
    original_order: number;
}

export default function AssignQuestionsPage() {
    const [tests, setTests] = useState<Test[]>([]);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [assigned, setAssigned] = useState<Assigned[]>([]);
    const [selectedTest, setSelectedTest] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // 🔹 Load tests on mount
    useEffect(() => {
        async function loadTests() {
            const { data, error } = await supabase
                .from("tests")
                .select("id, name")
                .order("id");

            if (!error && data) setTests(data);
        }
        loadTests();
    }, []);

    // 🔹 When a test is selected, load assigned + available questions
    useEffect(() => {
        if (!selectedTest) return;

        async function loadData() {
            setLoading(true);

            try {
                // Get all questions
                const { data: qData, error: qError } = await supabase
                    .from("questions")
                    .select("id, text, bank_id");

                if (qError) throw qError;
                setQuestions(qData || []);

                // Get assigned questions
                const { data: tqData, error: tqError } = await supabase
                    .from("test_questions")
                    .select("question_id, original_order")
                    .eq("test_id", selectedTest)
                    .order("original_order");

                if (tqError) throw tqError;
                setAssigned(tqData || []);
            } catch (err: any) {
                console.error("Error loading data:", err.message);
                setMessage("❌ Failed to load questions");
            } finally {
                setLoading(false);
            }
        }

        loadData();
    }, [selectedTest]);

    // 🔹 Toggle question assignment
    function toggleAssign(qid: number) {
        if (assigned.some((a) => a.question_id === qid)) {
            // Remove
            setAssigned(assigned.filter((a) => a.question_id !== qid));
        } else {
            // Add at the end
            setAssigned([
                ...assigned,
                { question_id: qid, original_order: assigned.length + 1 },
            ]);
        }
    }

    // 🔹 Save assignments
    async function handleSave() {
        if (!selectedTest) return;

        try {
            setLoading(true);
            setMessage("");

            // Clear existing
            await supabase
                .from("test_questions")
                .delete()
                .eq("test_id", selectedTest);

            // Insert new
            const { error } = await supabase.from("test_questions").insert(
                assigned.map((a, i) => ({
                    test_id: selectedTest,
                    question_id: a.question_id,
                    original_order: i + 1,
                }))
            );

            if (error) throw error;

            setMessage("✅ Questions assigned successfully!");
        } catch (err: any) {
            console.error("Error saving assignments:", err.message);
            setMessage("❌ Failed to save assignments");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="bg-white p-4 rounded shadow">
            <h2 className="text-lg font-bold mb-4">Assign Questions to Test</h2>

            {/* Select Test */}
            <label className="block mb-2 font-medium">Select Test</label>
            <select
                value={selectedTest ?? ""}
                onChange={(e) => setSelectedTest(Number(e.target.value))}
                className="border rounded p-2 mb-4 w-full"
            >
                <option value="">-- Choose Test --</option>
                {tests.map((t) => (
                    <option key={t.id} value={t.id}>
                        {t.name}
                    </option>
                ))}
            </select>

            {loading && <p>Loading...</p>}

            {/* Questions List */}
            {selectedTest && !loading && (
                <>
                    <table className="w-full border text-sm mb-4">
                        <thead>
                            <tr className="bg-gray-50 text-left">
                                <th className="p-2 border">Assigned?</th>
                                <th className="p-2 border">ID</th>
                                <th className="p-2 border">Text</th>
                            </tr>
                        </thead>
                        <tbody>
                            {questions.map((q) => {
                                const isAssigned = assigned.some((a) => a.question_id === q.id);
                                return (
                                    <tr
                                        key={q.id}
                                        className={isAssigned ? "bg-green-50" : ""}
                                    >
                                        <td className="p-2 border text-center">
                                            <input
                                                type="checkbox"
                                                checked={isAssigned}
                                                onChange={() => toggleAssign(q.id)}
                                            />
                                        </td>
                                        <td className="p-2 border">{q.id}</td>
                                        <td className="p-2 border">{q.text}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded"
                    >
                        {loading ? "Saving..." : "Save Assignments"}
                    </button>
                </>
            )}

            {message && <p className="mt-4">{message}</p>}
        </div>
    );
}

